import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ShoppingCart,
  Plus,
  Minus,
  Trash2,
  Package,
  CreditCard,
  User,
  Stethoscope,
  AlertCircle,
  RefreshCw,
  Calendar,
  Percent,
  Pill,
  ArrowLeft,
  AlertTriangle,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { useCart } from '@/contexts/CartContext';
import { useNavigate } from 'react-router-dom';
import { toast } from '@/hooks/use-toast';
import SearchableSelect, { SelectOption } from '@/components/ui/searchable-select';
import clinicService from '@/services/clinicService';
import { practitionerService } from '@/services/practitionerService';
import { useClinic } from '@/contexts/ClinicContext';
import { Practitioner } from '@/lib/types';
import { Patient } from '@/services/clinicService';
import { permissionCheck } from '@/lib/permission-check';

// Priority options (same as prescription pad)
const priorityOptions = [
  { value: -1, label: 'Low Precedence', icon: '↓' },
  { value: 1, label: 'Normal', icon: '→' },
  { value: 2, label: 'Elevated', icon: '↑' },
  { value: 3, label: 'Urgent', icon: '⚠️' },
];

// Skeleton Loader Component
const CartItemSkeleton: React.FC = () => (
  <div className='rounded-xl border border-slate-200 bg-white p-4'>
    <div className='flex animate-pulse items-center justify-between'>
      <div className='flex flex-1 items-center gap-4'>
        <div className='h-12 w-12 rounded-xl bg-slate-200'></div>
        <div className='flex-1'>
          <div className='mb-2 h-5 w-48 rounded bg-slate-200'></div>
          <div className='h-4 w-32 rounded bg-slate-200'></div>
        </div>
      </div>
      <div className='flex flex-col items-end gap-2'>
        <div className='h-6 w-20 rounded bg-slate-200'></div>
        <div className='h-8 w-24 rounded bg-slate-200'></div>
      </div>
    </div>
  </div>
);

// Cart Item Component
const CartItemCard: React.FC<{
  item: any;
  index: number;
  hasSubscription: boolean;
  discountPercentage?: number;
}> = ({ item, index, hasSubscription, discountPercentage = 0 }) => {
  const { updateQuantity, removeFromCart } = useCart();
  const { isAdmin, rolePermissions, isPractitioner } = useClinic();
  const canEdit = permissionCheck('storefront:update', rolePermissions, isAdmin || isPractitioner);
  const canCreate = permissionCheck('storefront:create', rolePermissions, isAdmin || isPractitioner);
  const canDoAction = canEdit || canCreate;

  const handleQuantityChange = (change: number) => {
    const newQuantity = item.quantity + change;
    if (newQuantity > 0) {
      updateQuantity(item.product_id, newQuantity);
    }
  };

  const handleRemove = () => {
    removeFromCart(item.product_id);
    toast({
      title: 'Item removed',
      description: `${item.medication_name} removed from cart.`,
    });
  };

  const getDiscountedPrice = () => {
    if (hasSubscription && discountPercentage > 0) {
      return item.price * (1 - discountPercentage / 100);
    }
    return item.price;
  };

  const getSavingsAmount = () => {
    if (hasSubscription && discountPercentage > 0) {
      return ((item.price * discountPercentage) / 100) * item.quantity;
    }
    return 0;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: -50, height: 0 }}
      transition={{ duration: 0.2, delay: index * 0.05 }}
      layout
    >
      <div className='rounded-xl border border-slate-200 bg-white p-4 transition-colors hover:border-slate-300'>
        <div className='flex items-start gap-4'>
          {/* Icon */}
          <div className='flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-primary/10'>
            <Pill className='h-6 w-6 text-primary' />
          </div>

          {/* Info */}
          <div className='min-w-0 flex-1'>
            <div className='mb-1 flex items-center gap-2'>
              <h4 className='font-semibold text-slate-900'>{item.medication_name}</h4>
              <Badge variant='outline' className='border-slate-200 text-xs'>
                {item.dose}
              </Badge>
            </div>

            <p className='mb-2 text-sm text-slate-500'>{item.program_name}</p>

            <div className='flex items-center gap-4 text-sm text-slate-500'>
              <span>{item.unit_of_measure}</span>
              {hasSubscription && discountPercentage > 0 ? (
                <>
                  <span className='line-through'>${item.price.toFixed(2)}</span>
                  <span className='font-medium text-emerald-600'>
                    ${getDiscountedPrice().toFixed(2)}
                  </span>
                </>
              ) : (
                <span>${item.price.toFixed(2)} each</span>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className='flex flex-col items-end gap-3'>
            <div className='text-right'>
              <div className='text-xl font-bold text-slate-900'>
                ${(getDiscountedPrice() * item.quantity).toFixed(2)}
              </div>
              {getSavingsAmount() > 0 && (
                <div className='text-xs font-medium text-emerald-600'>
                  Save ${getSavingsAmount().toFixed(2)}
                </div>
              )}
            </div>

            <div className='flex items-center gap-1'>
              <Button
                variant='outline'
                size='icon'
                onClick={() => handleQuantityChange(-1)}
                className='h-8 w-8 border-slate-200'
                disabled={!canDoAction}
              >
                <Minus className='h-3 w-3' />
              </Button>
              <span className='w-10 text-center text-sm font-medium'>{item.quantity}</span>
              <Button
                variant='outline'
                size='icon'
                onClick={() => handleQuantityChange(1)}
                className='h-8 w-8 border-slate-200'
                disabled={!canDoAction}
              >
                <Plus className='h-3 w-3' />
              </Button>
            </div>

            <Button
              variant='ghost'
              size='sm'
              onClick={handleRemove}
              className='h-8 px-2 text-red-500 hover:bg-red-50 hover:text-red-700'
              disabled={!canDoAction}
            >
              <Trash2 className='mr-1 h-3.5 w-3.5' />
              Remove
            </Button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// Main Cart Component
const Cart: React.FC = () => {
  const {
    items,
    cartCount,
    cartTotal,
    cartSubtotal,
    subscriptionDiscount,
    hasSubscriptions,
    subscription,
    priority,
    isLoading,
    canCheckout,
    selectedPatient,
    selectedPractitioner,
    setSelectedPatient,
    setSelectedPractitioner,
    setSubscription,
    setPriority,
    clearCart,
  } = useCart();

  const navigate = useNavigate();
  const { clinic } = useClinic();
  const clinicId = clinic?.id;
  const [patients, setPatients] = useState<Patient[]>([]);
  const [practitioners, setPractitioners] = useState<Practitioner[]>([]);
  const [loadingPatients, setLoadingPatients] = useState(false);
  const [loadingPractitioners, setLoadingPractitioners] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{
    patient?: string;
    practitioner?: string;
  }>({});

  const { isClinicStaff, isAdmin, rolePermissions, isPractitioner } = useClinic();
  const hasProtocolShowcase = items.some(item => item.display_mode === 'protocol_showcase');
  const showPractitionerDropdown = items.length > 0 && !hasProtocolShowcase;

  const handleNavigation = (url: string) => {
    if (isPractitioner) {
      navigate(`/practitioner-portal${url}`);
    } else {
      // Admin + clinic_staff both live at root.
      navigate(url);
    }
  };

  useEffect(() => {
    if (!clinicId) return;
    loadPatients();
    loadPractitioners();
  }, [clinicId]);

  // Clear subscription when cart has protocol_showcase (guided) items — subscription not offered for those
  useEffect(() => {
    if (hasProtocolShowcase && hasSubscriptions) {
      setSubscription(null);
    }
  }, [hasProtocolShowcase, hasSubscriptions, setSubscription]);

  // When dropdown is hidden (non-medication_select cart), clear any stale selection so
  // the backend can resolve DEFAULT_PRACTITIONER_EMAIL.
  useEffect(() => {
    if (!showPractitionerDropdown && selectedPractitioner) {
      setSelectedPractitioner(null);
    }
  }, [showPractitionerDropdown, selectedPractitioner, setSelectedPractitioner]);

  const loadPatients = async () => {
    try {
      setLoadingPatients(true);
      const patientsData = await clinicService.getPatientDropdown('', clinicId);
      setPatients(patientsData.patients || []);
    } catch (error) {
      toast({
        title: 'Error loading patients',
        description: 'Failed to load patients. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLoadingPatients(false);
    }
  };

  const loadPractitioners = async () => {
    if (!clinicId) return;
    try {
      setLoadingPractitioners(true);
      const practitionersData = await practitionerService.getPractitionerDropdownByClinic(clinicId);
      setPractitioners(practitionersData.data.practitioners ?? []);
    } catch (error) {
      toast({
        title: 'Error loading practitioners',
        description: 'Failed to load practitioners. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLoadingPractitioners(false);
    }
  };

  const handlePatientSearch = async (query: string) => {
    try {
      const searchResults = await clinicService.getPatientDropdown(query, clinicId);
      setPatients(searchResults.patients || []);
    } catch (error) {
      console.error('Error searching patients:', error);
    }
  };

  const handlePractitionerSearch = async (query: string) => {
    try {
      const searchResults = await practitionerService.getPractitionerDropdownByClinic(clinicId, {
        search: query,
      });
      setPractitioners(searchResults.data.practitioners);
    } catch (error) {
      console.error('Error searching practitioners:', error);
    }
  };

  const validateSelections = () => {
    const errors: { patient?: string; practitioner?: string } = {};

    if (!selectedPatient) {
      errors.patient = 'Please select a patient';
    }

    if (showPractitionerDropdown && !selectedPractitioner) {
      errors.practitioner = 'Please select a practitioner';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleClearCart = () => {
    clearCart();
    toast({
      title: 'Cart cleared',
      description: 'All items and selections have been removed from your cart.',
    });
  };

  const handleSubscriptionToggle = (enabled: boolean) => {
    if (enabled) {
      setSubscription({
        frequency: 'monthly',
        autoRenew: true,
        discountPercentage: 0,
      });
    } else {
      setSubscription(null);
    }
  };

  const handleFrequencyChange = (frequency: 'weekly' | 'monthly' | 'quarterly') => {
    if (subscription) {
      setSubscription({
        ...subscription,
        frequency,
      });
    }
  };

  const handleCheckout = async () => {
    if (!validateSelections()) {
      toast({
        title: 'Missing selections',
        description: 'Please select both a patient and practitioner before checkout.',
        variant: 'destructive',
      });
      return;
    }

    handleNavigation('/storefront/checkout');
  };

  const canEdit = permissionCheck('storefront:update', rolePermissions, isAdmin || isPractitioner);
  const canCreate = permissionCheck(
    'storefront:create',
    rolePermissions,
    isAdmin || isPractitioner || false
  );
  const canDoAction = canEdit || canCreate;

  if (isLoading) {
    return (
      <div className='space-y-6'>
        <div className='space-y-2'>
          <div className='h-8 w-48 animate-pulse rounded bg-slate-200'></div>
          <div className='h-4 w-32 animate-pulse rounded bg-slate-200'></div>
        </div>
        <div className='space-y-4'>
          {[...Array(3)].map((_, i) => (
            <CartItemSkeleton key={i} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='flex items-start gap-3'>
        <Button
          variant='outline'
          size='icon'
          onClick={() => navigate(-1)}
          className='h-9 w-9 flex-shrink-0 border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-slate-900'
          style={{ marginTop: '4px' }}
        >
          <ArrowLeft className='h-4 w-4' />
        </Button>
        <div>
          <h1 className='font-montserrat text-2xl font-bold tracking-tight text-slate-900'>
            Shopping Cart
          </h1>
          <p className='mt-1 text-sm text-slate-500'>
            {cartCount} {cartCount === 1 ? 'item' : 'items'} in your cart
          </p>
        </div>
      </div>

      {items.length === 0 ? (
        // Empty Cart State
        <Card className='border-slate-200'>
          <CardContent className='py-16'>
            <div className='text-center'>
              <div className='mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-slate-100'>
                <Package className='h-8 w-8 text-slate-400' />
              </div>
              <h2 className='mb-2 text-xl font-semibold text-slate-900'>Your cart is empty</h2>
              <p className='mx-auto mb-6 max-w-sm text-sm text-slate-500'>
                Browse our health programs to add medications to your cart.
              </p>
              <Button
                onClick={() => handleNavigation('/storefront')}
                className='bg-[rgb(35,31,32)] text-white hover:bg-[rgb(35,31,32)]/90'
              >
                <ShoppingCart className='mr-2 h-4 w-4' />
                Browse Programs
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className='grid grid-cols-1 gap-6 lg:grid-cols-3'>
          {/* Left Column - Forms and Items */}
          <div className='space-y-6 lg:col-span-2'>
            {/* Order Details Card */}
            <Card className='border-slate-200'>
              <CardHeader className='pb-4'>
                <CardTitle className='text-base font-semibold text-slate-900'>
                  Order Details
                </CardTitle>
              </CardHeader>
              <CardContent className='space-y-5'>
                {/* Patient Selection - disabled when pre-selected from storefront */}
                <div className='space-y-2'>
                  <Label className='text-sm text-slate-600'>Select Patient</Label>
                  <SearchableSelect
                    options={
                      patients.map(patient => ({
                        id: patient.id,
                        full_name: patient.full_name,
                        email: patient.email,
                        label: patient.full_name,
                        value: patient.id,
                        type: 'patient',
                      })) as SelectOption[]
                    }
                    value={selectedPatient as SelectOption}
                    onSelect={option => setSelectedPatient(option as Patient)}
                    onSearch={handlePatientSearch}
                    placeholder='Choose a patient...'
                    searchPlaceholder='Search patients...'
                    type='patient'
                    loading={loadingPatients}
                    error={validationErrors.patient}
                    disabled={loadingPatients || !canDoAction}
                  />
                </div>

                {showPractitionerDropdown && (
                  <div className='space-y-2'>
                    <Label className='text-sm text-slate-600'>Select Practitioner</Label>
                    <SearchableSelect
                      options={
                        practitioners.map(practitioner => ({
                          id: practitioner.id,
                          full_name: practitioner.full_name,
                          email: practitioner.email,
                          label: practitioner.full_name,
                          value: practitioner.id,
                          type: 'practitioner',
                        })) as SelectOption[]
                      }
                      value={selectedPractitioner as SelectOption}
                      onSelect={option => setSelectedPractitioner(option as Practitioner)}
                      onSearch={handlePractitionerSearch}
                      placeholder='Choose a practitioner...'
                      searchPlaceholder='Search practitioners...'
                      type='practitioner'
                      loading={loadingPractitioners}
                      error={validationErrors.practitioner}
                      disabled={loadingPractitioners || !canDoAction}
                    />
                  </div>
                )}

                {/* Validation Alert */}
                {!canCheckout && items.length > 0 && (
                  <Alert className='border-slate-200 bg-slate-50'>
                    <AlertCircle className='h-4 w-4 text-slate-500' />
                    <AlertDescription className='text-sm text-slate-600'>
                      {showPractitionerDropdown
                        ? 'Please select both a patient and practitioner to proceed with checkout.'
                        : 'Please select a patient to proceed with checkout.'}
                    </AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>

            {/* Priority Selection Card */}
            <Card className='border-slate-200'>
              <CardHeader className='pb-4'>
                <CardTitle className='flex items-center gap-2 text-base font-semibold text-slate-900'>
                  <AlertTriangle className='h-4 w-4 text-slate-500' />
                  Priority
                </CardTitle>
              </CardHeader>
              <CardContent className='space-y-3'>
                <Label className='text-sm text-slate-600'>
                  Priority (applies to all medications)
                </Label>
                <Select
                  value={priority.toString()}
                  onValueChange={value => setPriority(parseInt(value))}
                  disabled={!canDoAction}
                >
                  <SelectTrigger className='w-full border-slate-200'>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {priorityOptions.map(option => (
                      <SelectItem key={option.value} value={option.value.toString()}>
                        <span className='flex items-center gap-2'>
                          <span>{option.icon}</span>
                          <span>{option.label}</span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                {/* Priority Info */}
                <div className='rounded-xl border border-slate-200 bg-slate-50 p-3'>
                  <p className='text-xs text-slate-600'>
                    {priority === -1 && 'Low precedence prescriptions are processed within 48-72 hours.'}
                    {priority === 1 && 'Normal priority prescriptions are processed within 24 hours.'}
                    {priority === 2 && 'Elevated priority prescriptions are processed within 12 hours.'}
                    {priority === 3 && 'Urgent prescriptions are processed within 2-4 hours.'}
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Subscription Options — hidden for protocol_showcase (guided) items */}
            {!hasProtocolShowcase && (
              <Card className='border-slate-200'>
                <CardHeader className='pb-4'>
                  <CardTitle className='flex items-center gap-2 text-base font-semibold text-slate-900'>
                    <RefreshCw className='h-4 w-4 text-slate-500' />
                    Subscription Options
                  </CardTitle>
                </CardHeader>
                <CardContent className='space-y-4'>
                  {/* Subscription Toggle */}
                  <div className='flex items-center gap-3'>
                    <Switch
                      checked={hasSubscriptions}
                      onCheckedChange={handleSubscriptionToggle}
                      id='program-subscription'
                      disabled={!canDoAction}
                    />
                    <Label
                      htmlFor='program-subscription'
                      className='flex cursor-pointer items-center gap-2 text-sm font-medium'
                    >
                      Subscribe & Save on entire order
                      {hasSubscriptions && (
                        <Badge className='border-0 bg-primary/10 text-xs text-primary'>
                          {subscription?.discountPercentage}% OFF
                        </Badge>
                      )}
                    </Label>
                  </div>

                  {/* Subscription Frequency */}
                  {hasSubscriptions && (
                    <div className='space-y-3 pt-2'>
                      <div className='flex items-center gap-2'>
                        <Calendar className='h-4 w-4 text-slate-400' />
                        <Label className='text-sm text-slate-600'>Delivery Frequency:</Label>
                      </div>
                      <Select
                        value={subscription?.frequency || 'monthly'}
                        onValueChange={handleFrequencyChange}
                      >
                        <SelectTrigger className='w-full border-slate-200'>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value='weekly'>Weekly - Every 7 days</SelectItem>
                          <SelectItem value='monthly'>Monthly - Every 30 days</SelectItem>
                          <SelectItem value='quarterly'>Quarterly - Every 90 days</SelectItem>
                        </SelectContent>
                      </Select>

                      <div className='rounded-xl border border-primary/10 bg-primary/5 p-4'>
                        <div className='mb-2 flex items-center gap-2 text-primary'>
                          <RefreshCw className='h-4 w-4' />
                          <span className='text-sm font-medium'>Subscription Benefits</span>
                        </div>
                        <ul className='space-y-1 text-xs text-primary/80'>
                          <li>• {subscription?.discountPercentage}% discount on all medications</li>
                          <li>• Automatic refills - never run out</li>
                          <li>• Priority customer support</li>
                          <li>• Cancel or pause anytime</li>
                        </ul>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Cart Items */}
            <div className='space-y-4'>
              <div className='flex items-center justify-between'>
                <h2 className='font-semibold text-slate-900'>Cart Items</h2>
                <Button
                  variant='ghost'
                  size='sm'
                  onClick={handleClearCart}
                  className='h-8 text-red-500 hover:bg-red-50 hover:text-red-700'
                  disabled={!canDoAction}
                >
                  <Trash2 className='mr-1.5 h-3.5 w-3.5' />
                  Clear All
                </Button>
              </div>

              <AnimatePresence>
                <div className='space-y-3'>
                  {items.map((item, index) => (
                    <CartItemCard
                      key={item.product_id}
                      item={item}
                      index={index}
                      hasSubscription={hasSubscriptions}
                      discountPercentage={subscription?.discountPercentage || 0}
                    />
                  ))}
                </div>
              </AnimatePresence>
            </div>
          </div>

          {/* Right Column - Order Summary */}
          <div className='lg:col-span-1'>
            <div className='sticky top-4'>
              <Card className='border-slate-200'>
                <CardHeader className='pb-4'>
                  <CardTitle className='text-base font-semibold text-slate-900'>
                    Order Summary
                  </CardTitle>
                </CardHeader>
                <CardContent className='space-y-4'>
                  {/* Selected Patient & Practitioner */}
                  {(selectedPatient || selectedPractitioner) && (
                    <div className='space-y-2 border-b border-slate-100 pb-4'>
                      {selectedPatient && (
                        <div className='flex items-center gap-2'>
                          <User className='h-4 w-4 text-slate-400' />
                          <span className='text-sm text-slate-600'>
                            {selectedPatient.full_name}
                          </span>
                        </div>
                      )}
                      {showPractitionerDropdown && selectedPractitioner && (
                        <div className='flex items-center gap-2'>
                          <Stethoscope className='h-4 w-4 text-slate-400' />
                          <span className='text-sm text-slate-600'>
                            {selectedPractitioner.full_name}
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  <div className='flex justify-between text-sm'>
                    <span className='text-slate-600'>Consulting Fees ({cartCount} items)</span>
                    <span className='font-medium text-slate-900'>${cartSubtotal.toFixed(2)}</span>
                  </div>

                  {hasSubscriptions && subscriptionDiscount > 0 && (
                    <div className='flex justify-between text-sm'>
                      <span className='flex items-center gap-1 text-emerald-600'>
                        <Percent className='h-3 w-3' />
                        Subscription Savings
                      </span>
                      <span className='font-medium text-emerald-600'>
                        -${subscriptionDiscount.toFixed(2)}
                      </span>
                    </div>
                  )}

                  <div className='flex justify-between text-sm'>
                    <span className='text-slate-600'>Processing Fee</span>
                    <span className='font-medium text-emerald-600'>Free</span>
                  </div>

                  {hasSubscriptions && (
                    <div className='rounded-xl border border-primary/10 bg-primary/5 p-3'>
                      <div className='flex items-center gap-2 text-primary'>
                        <RefreshCw className='h-4 w-4' />
                        <span className='text-sm font-medium'>Subscription Benefits</span>
                      </div>
                      <ul className='mt-2 space-y-0.5 text-xs text-primary/80'>
                        <li>• Automatic refills</li>
                        <li>• Exclusive discounts</li>
                        <li>• Priority support</li>
                      </ul>
                    </div>
                  )}

                  <Separator className='bg-slate-100' />

                  <div className='flex justify-between'>
                    <span className='font-semibold text-slate-900'>Total Consulting Fees</span>
                    <span className='text-xl font-bold text-slate-900'>
                      ${cartTotal.toFixed(2)}
                    </span>
                  </div>

                  <Button
                    onClick={handleCheckout}
                    className='h-11 w-full bg-primary text-white hover:bg-primary/90'
                    disabled={!canCheckout || !canDoAction}
                  >
                    <CreditCard className='mr-2 h-4 w-4' />
                    Proceed to Checkout
                  </Button>

                  <p className='text-center text-xs text-slate-400'>
                    Secure prescription processing
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Cart;
